import { CircularProgressProps, extendVariants, CircularProgress as HeroCircularProgress } from '@heroui/react';
import type { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { HeroBaseProps } from '../utils';

type CircularProgressColor = 'gradual' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger';

// strokeWidth is in SVG viewbox units (viewbox=32). Formula: (desiredPx / circleSizePx) * 32
const DEFAULT_STROKE_WIDTH: Record<string, number> = {
  '2xs': 5.33, // 4px on 24px circle
  xs: 4.57, // 4px on 28px circle
  sm: 3.37, // 4px on 38px circle
  md: 4.36, // 6px on 44px circle
  lg: 3.69, // 6px on 52px circle
  xl: 2.18, // 6px on 88px circle
  '2xl': 2.53, // 9.5px on 120px circle
};

const SUFFIX_FONT_SIZE: Record<string, string> = {
  md: 'text-2xs',
  lg: 'text-2xs',
  xl: 'text-sm',
  '2xl': 'text-lg',
};

const buildValueLabel = (value: number | undefined, size: string): ReactNode => {
  const suffixClass = SUFFIX_FONT_SIZE[size];
  if (suffixClass == null) return undefined;
  return (
    <span>
      <span className="font-medium">{Math.round(value ?? 0)}</span>
      <span className={twMerge(suffixClass, 'text-secondary-400')}>%</span>
    </span>
  );
};

const getGradualColor = (value: number | undefined): CircularProgressProps['color'] => {
  // No value means indeterminate — there is no progress to grade, so grading it as 0% painted
  // every spinner red. Only a real percentage gets the danger/warning/success scale.
  if (value == null) return 'primary';
  if (value < 35) return 'danger';
  if (value < 70) return 'warning';
  return 'success';
};

// For all CircularProgress props see https://v2.heroui.com/docs/components/circular-progress
export const CircularProgress = extendVariants(
  (
    props: HeroBaseProps<
      CircularProgressProps,
      | 'formatOptions'
      | 'label'
      | 'showValueLabel'
      | 'size'
      | 'value'
      | 'valueLabel'
      | 'strokeWidth'
      | 'disableAnimation'
    > & {
      /** Color mode. "gradual" changes color based on value (danger→warning→success). */
      color?: CircularProgressColor;
    },
  ) => {
    const { color = 'gradual', size = 'lg', strokeWidth, valueLabel, showValueLabel, ...rest } = props;

    const resolvedColor = color === 'gradual' ? getGradualColor(props.value) : color;
    const resolvedStrokeWidth = strokeWidth ?? DEFAULT_STROKE_WIDTH[size] ?? 4;
    const resolvedValueLabel = valueLabel ?? (showValueLabel ? buildValueLabel(props.value, size) : undefined);

    return (
      <HeroCircularProgress
        {...rest}
        size={size}
        color={resolvedColor}
        strokeWidth={resolvedStrokeWidth}
        showValueLabel={showValueLabel}
        valueLabel={resolvedValueLabel}
      />
    );
  },
  {
    defaultVariants: {
      size: 'lg',
    },
    variants: {
      size: {
        '2xs': {
          svg: 'w-6 h-6 [&_circle:first-child]:[stroke-width:8]',
        },
        xs: {
          svg: 'w-7 h-7 [&_circle:first-child]:[stroke-width:6.86]',
        },
        sm: {
          svg: 'w-9 h-9 [&_circle:first-child]:[stroke-width:5.05]',
        },
        md: {
          svg: 'w-11 h-11 [&_circle:first-child]:[stroke-width:5.82]',
          value: 'text-xs font-medium',
        },
        lg: {
          svg: 'w-13 h-13 [&_circle:first-child]:[stroke-width:4.92]',
          value: 'text-xs font-medium',
        },
        xl: {
          svg: 'w-22 h-22 [&_circle:first-child]:[stroke-width:2.91]',
          value: 'text-xl font-medium',
        },
        '2xl': {
          svg: 'w-30 h-30 [&_circle:first-child]:[stroke-width:3.20]',
          value: 'text-3xl font-medium',
        },
      },
    },
  },
);
